import { useEffect, useMemo, useState } from 'react'
import { startCase, toLower, unionBy } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { GridColumns, useGridApiRef } from '@mui/x-data-grid-pro_modern'
import { Box, Button, Divider, Stack, Typography } from '@mui/material_modern'
import { setAssets, UIAssetModel } from 'state/dispatch'

import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'

import { useGetAssetsByCategoryQuery } from 'services/physicalAssets'
import {
  ASSET_STATUSES,
  DISPATCH_TICKET_ENUM,
  DISPATCH_TICKET_STATUSES,
  DISPATCH_TICKET_TYPES,
  DT_STATUS_ENUM,
  PAGE_SIZE,
} from 'utils/constants'
import { formatAssetForGrid } from 'components/containers/load-board/utils/formatFunctions'
import { ASSET_STATUS_ICONS_DICT } from 'components/containers/load-board/utils/constants'
import { convertShippingInfoToCityStateZip, formatAddress } from 'utils/data-manipulation/valueFormatter'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { selectAssetSelectionBladeProps } from 'state/ui/selectors.ts'
import { ModalBladeProps } from 'components/layouts/ContentLayout.tsx'

export interface AssetSelectionBladeModalProps {
  type: DISPATCH_TICKET_ENUM
  selectedLocation: {
    id: string
    address: string
    city: string
    state: string
    zip: string
    notes: string
    name: string
  }
  fromLocationId: string
  ticketStatus: (typeof DISPATCH_TICKET_STATUSES)[keyof typeof DISPATCH_TICKET_STATUSES]['id']
}

const AssetSelectionBlade: React.FC<ModalBladeProps> = ({ onFormStateChange, onHideModal }) => {
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()

  const { organization, yardDetailsList } = useSelector(state => (state as any).auth)

  const { type, selectedLocation, fromLocationId, ticketStatus } = useAppSelector(selectAssetSelectionBladeProps)

  // list of assets local to this React component
  const [selectedAssets, setSelectedAssets] = useState<string[]>([])

  const isSelectingForPickup =
    type === DISPATCH_TICKET_TYPES.PICK_UP.id || type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id
  const dispatchReduxState = useAppSelector(state => state.dispatch)
  // get asset list according to redux global state
  const assetList = useMemo(() => {
    if (type === DISPATCH_TICKET_TYPES.PICK_UP.id) {
      return dispatchReduxState.pickupAssets
    }
    if (type === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
      return dispatchReduxState.dropoffAssets
    }
    if (type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
      return dispatchReduxState.pickAndReturnAssets
    }
    if (type === DISPATCH_TICKET_TYPES.DELIVERY.id) {
      return dispatchReduxState.deliveryAssets
    }
    if (type === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
      return dispatchReduxState.liveLoadAssets
    }
    return []
  }, [dispatchReduxState, type])

  /**
   * This is needed to deal with assets being removed or added from a ticket prior to saving.
   * It sets the true status of an asset, because this isn't automatically updated. This is so the list in the
   * selection blade shows the true location of that asset based on if it attached to the dispatch ticket, even if
   * the backend data hasn't been updated yet
   * It only needs to edit givenAssets if the ticket is in progress.
   * @param givenAssets --> The list of assets we wan't to update status for displaying in the list
   * @param selectedAssets --> The list of assetIds that are currently selected
   * @param unselectedStatus --> Optional. This will be either deployed or inventory.
   */
  const cleanAssetStatuses = (givenAssets, selectedAssets, unselectedStatus?) => {
    // We only need to update the status for the blade list if the ticket is in progress
    // If not, or the list to display is undefined, we just return the list as-is
    if (ticketStatus === DISPATCH_TICKET_STATUSES[DT_STATUS_ENUM.IN_PROGRESS].id && Array.isArray(givenAssets)) {
      // If the list is empty, we return the empty list
      if (givenAssets.length < 1) {
        return []
      }

      // If there isn't a default status to set to, we just copy the given assets' status, because
      // it means these are directly from the backend and unselected
      if (unselectedStatus === undefined) {
        unselectedStatus = givenAssets[0].assetStatus.status
      }

      return givenAssets.map(givenAsset => {
        // This tells us if this given asset is selected currently
        const foundAsset = selectedAssets.find(selectedAssetId => selectedAssetId === givenAsset.id)

        // If the asset is selected, we want to reset the status to show that it is in-progress
        // If not selected, we set to the unselected status, which is deployed or inventory
        return {
          ...givenAsset,
          assetStatus: givenAsset.assetStatus
            ? {
                ...givenAsset.assetStatus,
                status: foundAsset ? ticketStatus : unselectedStatus,
              }
            : undefined,
          status: foundAsset ? ticketStatus : unselectedStatus,
        }
      })
    } else {
      return givenAssets
    }
  }

  // get initial asset list according to redux global state
  const initialAssetList = useMemo(() => {
    if (type === DISPATCH_TICKET_TYPES.PICK_UP.id) {
      return cleanAssetStatuses(dispatchReduxState.initialPickupAssets, selectedAssets, ASSET_STATUSES.DEPLOYED.id)
    }
    if (type === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
      return cleanAssetStatuses(dispatchReduxState.initialDropoffAssets, selectedAssets, ASSET_STATUSES.INVENTORY.id)
    }
    if (type === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
      return cleanAssetStatuses(
        dispatchReduxState.initialPickAndReturnAssets,
        selectedAssets,
        ASSET_STATUSES.DEPLOYED.id,
      )
    }
    if (type === DISPATCH_TICKET_TYPES.DELIVERY.id) {
      return cleanAssetStatuses(dispatchReduxState.initialDeliveryAssets, selectedAssets, ASSET_STATUSES.INVENTORY.id)
    }
    if (type === DISPATCH_TICKET_TYPES.LIVE_LOAD.id) {
      return cleanAssetStatuses(dispatchReduxState.initialLiveLoadAssets, selectedAssets, ASSET_STATUSES.INVENTORY.id)
    }
    return []
  }, [dispatchReduxState, type, selectedAssets])

  const handleSelectedAssetsChange = newSelectedAssets => {
    setSelectedAssets(prevModel => {
      if (newSelectedAssets.length === 0) {
        return prevModel.length === 1 ? [] : prevModel
      }
      return newSelectedAssets
    })
  }
  useEffect(() => {
    const currentIds = (
      assetList?.map((asset: UIAssetModel) => asset.id || asset.asset?.id || asset.assetId) || []
    ).filter(Boolean) as string[]

    if (currentIds.length) {
      setSelectedAssets(currentIds)
    }
  }, [assetList])

  const { data: containerAssetData, isLoading: isLoadingContainerData } = useGetAssetsByCategoryQuery({
    assetCategory: 'CONTAINER',
    filter: JSON.stringify({
      locationId: fromLocationId,
    }),
  })

  const isLoading = isLoadingContainerData

  const { joinedAssetData, formattedAssetData } = useMemo(() => {
    const cleanedContainerAssetData = cleanAssetStatuses(containerAssetData?.Items, selectedAssets)
    const joinedAssets: any = unionBy(initialAssetList || [], cleanedContainerAssetData || [], 'id')
    const yardLocationData = yardDetailsList?.find(({ locationId }) => locationId === fromLocationId)
    const locationIsYard = !!yardLocationData

    return {
      joinedAssetData: joinedAssets,
      formattedAssetData: joinedAssets.map(asset =>
        formatAssetForGrid(
          asset,
          locationIsYard
            ? `${startCase(toLower(organization as any))} ${startCase(yardLocationData?.name) || ''}`
            : selectedLocation,
        ),
      ),
    }
  }, [initialAssetList, containerAssetData, selectedAssets])

  const onSubmit = () => {
    const finalAssets = joinedAssetData?.filter(asset => selectedAssets.includes(asset.id))
    dispatch(setAssets({ ticketType: type, assets: finalAssets }))
    onHideModal()
  }

  useEffect(() => onFormStateChange(false), [])

  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const assetColumns = useMemo<GridColumns>(
    () => [
      {
        field: 'assetName',
        headerName: 'Container Number',
        flex: 1,
        editable: false,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        editable: false,
        renderCell: ({ row }) => {
          const { status, statusName } = row || {}
          return (
            <>
              {ASSET_STATUS_ICONS_DICT[status]}
              <Typography pl={1} variant="body2">
                {statusName}
              </Typography>
            </>
          )
        },
      },
      {
        field: 'type',
        headerName: 'Container Type',
        flex: 1,
        editable: false,
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 2,
        editable: false,
      },
      {
        field: 'daysOnSite',
        headerName: 'Days on Site',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        editable: false,
      },
    ],
    [],
  )

  return (
    <Stack height="100vh">
      <FormModalHeading
        title={`${DISPATCH_TICKET_TYPES[type]?.name} Container`}
        backButtonPosition="right"
        onBack={() => onHideModal()}
      />
      <Divider />
      <Typography variant="body1" px={4} py={2}>
        {`Select a container to ${DISPATCH_TICKET_TYPES[type]?.spacedName?.toLowerCase()}
        ${
          selectedLocation
            ? ` ${isSelectingForPickup ? 'from' : 'to'} ${selectedLocation.name} located at
        ${formatAddress(selectedLocation)}, ${convertShippingInfoToCityStateZip(selectedLocation)}.`
            : '.'
        }
        If you do not see the container you are looking for, you may need to change its location. `}
        <br />
        <Link to="/app/assets/containers" target="_blank">
          View all containers
        </Link>
      </Typography>
      <Box px={4} py={2}>
        <BasicDataGridV2
          pagination
          apiRef={apiRef}
          editMode="row"
          density="compact"
          checkboxSelection
          disableColumnMenu
          loading={isLoading}
          pageSize={pageSize}
          autoPageSize={false}
          columns={assetColumns}
          selectionModel={selectedAssets}
          rowsPerPageOptions={[10, 20, 30]}
          rows={formattedAssetData}
          onSelectionModelChange={handleSelectedAssetsChange}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        />
      </Box>
      <Divider sx={{ marginTop: 'auto' }} />
      <Stack direction="row" p={2} spacing={2}>
        <Button variant="contained" onClick={onSubmit}>
          Save
        </Button>
        <Button variant="outlined" onClick={() => onHideModal()}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  )
}

export default AssetSelectionBlade
